import { useEffect, useState } from 'react';
import useSWR from 'swr';
import Layout from '@layout/index';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { useAxios } from 'use-axios-client';

// ---------------------------------------------------------
import classNames from 'classnames';
import Listing from '@lib/models/listing';
import {
  formatMoney,
  formatNumber,
  getSlug,
  fetcher,
  truncateListingName,
  parseDateWithFormatMMMDDAndTime
} from '@lib/utils';

import {
  fetchAssociatedAgent,
  execAssociatedAgentQuery,
  getAssociatedAgentItem
} from '@lib/associatedAgent';

import { updateIsSibcyListingFilter } from '../../../store/slices/miscSlice';
import communityLookup from '@src/data/community-info.json';
import builderDevelopments from '@src/data/builder-development-info.json';
import { useAgentData } from '@lib/hooks';

// ---------------------------------------------------------

import Accordion from '@components/accordion';
import Button from '@components/button';
import Container from '@layout/container';
import Image from '@components/image';
import NotificationCard from '@components/notification-card';
import Modal from '@components/modal';
import MortgageCalculator from '@components/mortgage-calculator';
import NotesCardModal from '@components/notes/notes-card-modal';
import PostList from '@components/post-list';
import BrokerReciprocityCard from '@components/broker-reciprocity-card';
import PropertyImages from '@components/property-images';
import EnhancedContactForm from '@components/forms/enhanced';
import FormRealtorCards from '@components/forms/enhanced/realtor-cards';
import SellWithSibcy from '@components/sell-with-sibcy';
import ShareModal from '@components/modal/share-modal';
import SimilarProperties from '@components/similar-properties';
import TabSlider from '@components/tab-slider';
import Video from '@components/video';
import Map from '@components/map';
import Link from '@components/link';
import OpenHousesDropdown from '@components/openhouses-dropdown';
import RequestATourModal from '@components/request-a-tour-modal/component';
import TopicCarousel from '@components/topic-carousel';
import ContactForm from '@components/forms';
import OpenHouseModal from '@components/open-house-modal';
import { AddToCalendarButton } from 'add-to-calendar-button-react';

// ---------------------------------------------------------

import {
  page,
  page_container,
  address_wrapper,
  property_header_wrapper,
  property_header,
  address_container,
  stat_container,
  stat_container_mobile,
  stat,
  stat_wrapper,
  price_container,
  top,
  summary,
  details,
  section_header,
  left,
  top_section,
  overview_heading,
  right,
  right_margin_top_600,
  buttons,
  mobile_button,
  notification_modal,
  notes_modal,
  notification_card,
  visitor_stats,
  featured_section,
  featured_section_multiple_photos,
  featured_section_singular_photo,
  tabs,
  middle,
  calculator,
  bottom,
  browser_fix,
  listing_courtesy_of,
  refresh_date,
  notification_header,
  copyright_section,
  top_content_container,
  base_alert,
  base_alert_coming_soon,
  base_alert_pending,
  base_alert_price_change,
  base_alert_listed_on,
  directions_container,
  virtual_tour,
  directions_link,
  alerts_container,
  center,
  grid_container,
  featured_section_video,
  sticky_footer,
  is_mobile,
  is_desktop,
  concession_note_container,
  seller_concession_note,
  add_to_calendar,
  breadcrumb_container,
  breadcrumbs
} from './styles.module.scss';
import Icon from '@components/icon';

// ---------------------------------------------------------

import ListingDetailPageTemplate from '@templates/ListingDetailPage';

// ---------------------------------------------------------

const getCanonicalSlug = (fullCanonical) => {
  const canonicalPath = new URL(fullCanonical).pathname;
  const parts = canonicalPath.split('/');
  const slug = parts.slice(3).join('/');
  return slug;
};

export async function getServerSideProps(context) {
  // set caching headers to avoid caching in the browser
  context.res.setHeader('Cache-Control', 'no-store');

  const { agentid } = context.query;
  const { id: slugId, slug: slugArray } = context.params;
  const id = parseInt(slugId);

  const listing = new Listing(id);
  const details = await listing.details();
  let associatedAgent = null;

  // 404 if no pageData for the ID
  if (!details) {
    return {
      notFound: true
    };
  }

  // Redirect if the canonical URL doesn't match
  const { canonicalUrl } = details;
  const canonicalFromAPI = getCanonicalSlug(canonicalUrl);

  let slugAddress;
  if (slugArray?.length > 1) {
    slugAddress = slugArray.join('/');
  } else if (slugArray?.length === 1) {
    slugAddress = slugArray[0];
  } else {
    slugAddress = null;
  }

  let slugCheck = encodeURIComponent(slugAddress);

  // Apostrophe Edge Case
  slugCheck = slugCheck.replace("'", '%27');

  if (canonicalFromAPI !== slugCheck) {
    const { resolvedUrl } = context;

    let destinationUrl;
    if (slugAddress) {
      destinationUrl = resolvedUrl.replace(slugCheck, canonicalFromAPI);
    } else {
      destinationUrl = `/listing/${id}/${canonicalFromAPI}`;
      const queryString = resolvedUrl.split('?')[1];
      if (queryString) {
        destinationUrl += `?${queryString}`;
      }
    }

    return {
      redirect: {
        destination: destinationUrl,
        permanent: false
      }
    };
  }

  const fullCommunityLookup = Object.entries(communityLookup).map((x) => ({
    lookups: (x[1].fallbackSlugs || []).concat([x[0]]),
    slug: x[0]
  }));

  const listingCommunity =
    communityLookup[
      fullCommunityLookup.find((x) => x.lookups.some((y) => y === details?.communitySlug))?.slug ||
        details.communityFallbackSlug
    ];

  const builder =
    builderDevelopments.find((x) => x.category == 'Builder' && x.slug == details.builderSlug) || {};
  builder.url = `/builder/${builder?.slug}`;
  const development =
    builderDevelopments.find(
      (x) => x.category == 'Development' && x.slug == details.developmentSlug
    ) || {};
  development.url = `/development/${development?.slug}`;

  const nearbyCommunities = listingCommunity.nearbyCommunities.map((slug) => communityLookup[slug]);

  const similarProperties = await listing.getSimilarListings(details.similarListingIds);
  const interestRates = {
    15: (await listing.interest(15)).currentRate || '6',
    30: (await listing.interest(30)).currentRate || '5.5',
    lastUpdatedOn: (await listing.interest(30)).lastUpdatedOn
  };

  if (agentid) {
    associatedAgent = await fetchAssociatedAgent({ agentId: parseInt(agentid) });
  }

  return {
    props: {
      ...details,
      builder,
      development,
      interestRates,
      listingCommunity,
      nearbyCommunities,
      similarProperties,
      associatedAgent: getAssociatedAgentItem(associatedAgent)
    }
  };
}

// ---------------------------------------------------------

const ListingDetail = (props) => {
  return <ListingDetailPageTemplate {...props} />;
};

export default ListingDetail;
